import { AppBar, Toolbar, Typography, Box, Container, Paper, Skeleton, Card, CardContent } from "@mui/material";
import React, { useEffect, useState } from "react";
import OnpNavigation from "./OnpNavigation";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import { notifyError, onpRequest } from "../../util";
import { CartStorage } from "./OnpLocalStorage";
import { AccessTime } from "@mui/icons-material";
import { translate } from "../../i18n/customI18nProvider";

export const OnpContext = React.createContext(null);

export default function OnpLayout({ children }) {
    const history = useHistory();

    const [barInfo, setBarInfo] = useState({ eventName: '', name: '' });
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState([]);
    const [timer, setTimer] = useState(null);
    const MAX_WIDTH = 800;
    const {barId, table_number} = useParams();
    let cartStorage = new CartStorage(barId);

    useEffect(() => {
        fetchBarInfo();
        setOrder(cartStorage.getConsumptions());
    }, [barId]);

    const fetchBarInfo = async () => {
        if (!barId) return;
        try {
            const response = await onpRequest(barId, '/barInfo', 'GET');
            if(response.error) {
                //Redirect to not found page
                window.location.href = '/404';
                return;
            }
            setBarInfo(response);
            setLoading(false);
            const now = Date.now();
            const startDate = new Date(response.startDate).getTime();
            setTimer(startDate - now);
            if(startDate - now < 0) {
                setTimer(0);
            } else {
                const interval = setInterval(() => {
                    const now = Date.now();
                    const timeLeft = startDate - now;
                    if (timeLeft <= 0) {
                        clearInterval(interval);
                        fetchBarInfo();
                    } else {
                        setTimer(timeLeft);
                    }
                }, 1000);
                return () => clearInterval(interval);
            }
        } catch (error) {
            notifyError(translate("custom.an_error_occured_while_fetching_order_and_pay_details"));
        }
    }

    const navigate = (path) => {
        fetchBarInfo();
        history.push(path);
    }

    const consumptionChange = (consumptionId, quantity) => {
        cartStorage.updateQuantity(consumptionId, quantity);
        setOrder(cartStorage.getConsumptions());
    };

    const clearOrder = () => {
        cartStorage.clear();
        setOrder(cartStorage.getConsumptions());
    }

    const getConsumptionQuantity = (consumptionId) => {
        const item = order.find((item) => item.id === consumptionId);
        return item?.quantity || 0;
    }

    const getConsumptions = () => {
        return order;
    }

    const formatTime = (milliseconds) => {
        const totalSeconds = Math.ceil(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const hours = Math.floor(minutes / 60);
        const days = Math.floor(hours / 24);

        if (days > 0) {
            return `${days}J ${hours % 24}h ${minutes % 60}m`;
        }

        if (hours > 0) {
            return `${hours}h ${minutes % 60}m ${seconds}s`;
        }

        return `${minutes}m ${seconds}s`;
    }

    const renderContent = () => {
        if(loading) {
            let skeletons = [];
            for(let i = 0; i < 5; i++) {
                skeletons.push(i);
            }
            return (
                <Box>
                    <Skeleton />
                    <Skeleton />
                    <div style={{marginTop: "50px"}}></div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {skeletons.map((skeleton) => (
                            <Card key={skeleton} variant="outlined" sx={{ borderRadius: 2, boxShadow: 1, flexShrink: 0 }}>
                                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, padding: 2 }}>
                                    <Skeleton variant="circular" width={40} height={40} />
                                    <Box sx={{ flex: 1 }}>
                                        <Skeleton width="80%" />
                                        <Skeleton width="60%" />
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </Box>
            )
        }
        if(barInfo && barInfo.endDate && new Date(barInfo.endDate) < new Date()) {
            return (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                    <Paper elevation={0}
                        sx={{
                            p: 6,
                            maxWidth: MAX_WIDTH,
                            width: "100%",
                            textAlign: "center",
                            borderRadius: 4,
                            border: "1px solid #e0e0e0",
                            bgcolor: "#ffffff",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <AccessTime sx={{ fontSize: 40, color: "#9e9e9e", mb: 2 }} />

                            <Typography variant="overline" sx={{ letterSpacing: 2, color: "text.secondary" }}>
                                {translate("custom.closing").toUpperCase()}
                            </Typography>
                        </div>

                        <Typography variant="h4" sx={{ fontWeight: 600, my: 2 }}>
                            {translate("custom.point_of_sale_closed")}
                        </Typography>

                        <Typography variant="body1" sx={{ color: "text.secondary" }}>
                            {translate("custom.thanks_for_using_order_and_pay")}
                        </Typography>
                    </Paper>
                </Box>
            )
        } else if (timer != null && timer > 0) {
            return (
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
                    <Paper elevation={0}
                        sx={{
                            p: 6,
                            maxWidth: MAX_WIDTH,
                            width: "100%",
                            textAlign: "center",
                            borderRadius: 4,
                            border: "1px solid #e0e0e0",
                            bgcolor: "#ffffff",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                            <AccessTime sx={{ fontSize: 40, color: "#9e9e9e", mb: 2 }} />

                            <Typography variant="overline" sx={{ letterSpacing: 2, color: "text.secondary" }}>
                                {translate("custom.opening_soon").toUpperCase()}
                            </Typography>
                        </div>

                        <Typography variant="h4" sx={{ fontWeight: 600, my: 2, fontVariantNumeric: "tabular-nums" }}>
                            {formatTime(timer)}
                        </Typography>

                        <Typography variant="body1" sx={{ color: "text.secondary" }}>
                            {translate("custom.point_of_sale_not_yet_open")}
                            <br />
                            {translate("custom.wait_some_times")}
                        </Typography>
                    </Paper>
                </Box>
            )
        } else {
            return (
                <>
                    {children}
                </>
            )
        }
    }

    return (
        <OnpContext.Provider value={{ barInfo, order, consumptionChange, clearOrder, getConsumptionQuantity, getConsumptions, maxWidth: MAX_WIDTH, navigate }}>
            <div style={{ maxWidth: MAX_WIDTH, margin: 'auto', display: 'flex', flexDirection: 'column', paddingLeft: 8, paddingRight: 8 }}>
                <header style={{ marginBottom: 16, marginTop: 16}}>
                    <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700 }}>{loading ? "" : barInfo.name + ' - ' + table_number}</h1>
                    <div style={{ marginTop: 6, color: 'rgba(0,0,0,0.65)', fontSize: '0.9rem' }}>{barInfo.eventName}</div>
                </header>
                {
                    renderContent()
                }
                <OnpNavigation />
            </div>
        </OnpContext.Provider>
    );
}